<script lang="ts">
	import StickyHeader from '$src/lib/components/display/StickyHeader.svelte'
	import { getContextClient, queryStore } from '@urql/svelte'
	import type { PageData } from './$types'
	import {
		ProfileDocument,
		type ProfileQuery,
		type ProfileQueryVariables,
	} from '$lib/queries/generated/QueryProfile'
	import { browser } from '$app/environment'
	import { onDestroy, onMount } from 'svelte'
	import blockState from '$lib/stores/block-state'
	import PanelBody from '$lib/components/itineraries/PanelBody.svelte'
	import PanelHeader from '$lib/components/itineraries/PanelHeader.svelte'
	import Spinner from '$lib/components/loading/Spinner.svelte'
	import { navigating, page } from '$app/state'
	import { goto, preloadData } from '$app/navigation'
	import SleekButton from '$lib/components/buttons/SleekButton.svelte'
	import { track } from '$lib/utils/track'
	import DescriptionDocument from '$lib/components/forms/DescriptionDocument.svelte'
	import {
		UpdateProfileDocument,
		type UpdateProfileMutation,
		type UpdateProfileMutationVariables,
	} from '$lib/queries/generated/UpdateProfile'
	import ProfilePicture from '$lib/components/profiles/ProfilePicture.svelte'
	import TitleContainer from '$lib/components/itineraries/TitleContainer.svelte'
	import ImmutableTitle from '$lib/components/itineraries/ImmutableTitle.svelte'
	import SocialAccounts from '$lib/components/profiles/SocialAccounts.svelte'
	import PanelLoading from '$lib/components/itineraries/PanelLoading.svelte'
	import VerifiedIcon from '$lib/components/icons/VerifiedIcon.svelte'
	import ExploreTripCard from '$lib/components/itineraries/ExploreTripCard.svelte'
	import DescriptionForm from '$lib/components/modals/DescriptionForm.svelte'
	import debounce from 'lodash-es/debounce'
	import ReviewsList from '$components/itineraries/ReviewsList.svelte'

	interface Props {
		data: PageData
		nextIntentID?: string | null
	}

	let { data, nextIntentID = $bindable(null) }: Props = $props()

	const client = getContextClient()

	function toggleEdit() {
		if (!profile?.isCurrentUser) return

		const origin = window.location.origin
		let newUrl = `/profile/${username}`

		const url = new URL(newUrl, origin)

		if (canEdit === 'true') {
			url.searchParams?.delete('edit')
		} else {
			url.searchParams?.set('edit', 'true')
		}

		if (!browser) return

		goto(url, { keepFocus: false }).then(() => {})
	}

	function buildListingURL(listingID: string) {
		let nextURL = new URL(`/explore/${listingID}`, page?.url)
		return nextURL
	}

	function handleClickTrip(tripId: string) {
		const listing = profile?.publishedListings.find(
			(listing) => listing?.currentTrip?.id === tripId,
		)
		track('View Listing', { listingId: listing?.id })
		let nextURL = buildListingURL(listing?.id)

		setTimeout(() => {
			if (navigating) {
				nextIntentID = listing?.id
			}
		}, 100)

		goto(nextURL.toString())
	}

	function preloadListingPage(listingID: string) {
		let nextURL = buildListingURL(listingID)
		return preloadData(nextURL.toString())
	}

	// function handleClosePage() {
	// 	window.history.back()
	// }

	const updateProfile = (vars: UpdateProfileMutationVariables) =>
		client.mutation<UpdateProfileMutation, UpdateProfileMutationVariables>(
			UpdateProfileDocument,
			vars,
		)

	// The core update function
	const updateProfileBio = async (content: string) => {
		if (!content || !profile) return

		try {
			track('Update Profile', { profileId: profile.username })
			const result = await updateProfile({
				input: { bio: content },
			})
			return result
		} catch (error) {
			console.error('Error updating profile:', error)
		}
	}

	// Create the debounced function
	const debouncedUpdateProfileBio = debounce(updateProfileBio, 1000)

	// Clean up the debounced function when component is destroyed
	onDestroy(() => {
		debouncedUpdateProfileBio.cancel()
	})

	// The event handler function
	function handleProfileBioUpdate(content: string, error: boolean) {
		if (error) return
		debouncedUpdateProfileBio(content)
	}

	function handleImageLoadError() {
		imageUrl = '/visuals/icons/default-user.svg'
	}

	function updateName(title: string, error: boolean) {
		if (error) return
		track('Update Profile Name', { name: title })
		updateProfile({ input: { fullName: title } }).then((res) => {
			// console.log('res', res.data.updateTrip.title)
		})
	}

	function updateSocialUsername(platform: string, username: string) {
		if (username === 'not set' || username === null || username === '' || username === 'Add a Name')
			return
		track('Update Profile Name', { name: username, platform: platform })
		let inputObj = {}
		switch (platform) {
			case 'instagram':
				inputObj = { instagramUsername: username }
				break
			case 'tiktok':
				inputObj = { tiktokUsername: username }
				break
			case 'youtube':
				inputObj = { youtubeUsername: username }
				break
			case 'rss':
				inputObj = { websiteUrl: username }
				break
		}
		updateProfile({ input: inputObj }).then((res) => {
			// console.log('res', res.data.updateTrip.title)
		})
	}

	onMount(() => {
		if (blockState) {
			blockState.setMapState('trip')
		}
	})
	let username = $derived(data?.username)
	let profileStore = $derived(
		queryStore<ProfileQuery, ProfileQueryVariables>({
			client,
			query: ProfileDocument,
			variables: { id: username },
			pause: !browser || !username,
		}),
	)
	let profile = $derived($profileStore?.data?.profile)
	let imageUrl = $state('/visuals/icons/default-user.svg')
	$effect(() => {
		imageUrl = profile?.currentProfilePictureUrl
	})
	let canEdit = $derived(new URL(page?.url)?.searchParams?.get('edit') ?? null)
	let isImmutable = $derived(!profile?.isCurrentUser || canEdit !== 'true')

	let reviews = $derived(
		profile?.publishedListings
			?.flatMap((listing) => listing?.reviews)
			?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) ?? [],
	)
</script>

{#if $profileStore?.fetching || $profileStore?.data == null}
	<PanelLoading />
{:else}
	<PanelHeader>
		<div class="flex items-center justify-between w-full">
			<div class="ml-0 flex items-center pr-4 max-w-[50%]">
				<div class="relative mr-2 lg:mr-3 flex-shrink-0">
					<img
						src={imageUrl}
						onerror={handleImageLoadError}
						alt=""
						class="h-8 w-8 rounded-full bg-brand-gray-1 ring-1 ring-brand-gray-1"
					/>
					{#if profile?.hasStore}<VerifiedIcon class="absolute -bottom-1 -right-1 h-4 w-4" />{/if}
				</div>
				<div class="flex flex-col items-start w-full">
					<p class="text-[12px] leading-3 tracking-wide text-brand-gray-4">Profile</p>

					<p
						class="text-xs font-medium leading-4 truncate text-ellipsis w-full text-black dark:text-white"
					>
						{profile?.currentName != '' && profile?.currentName != null
							? profile?.currentName
							: profile?.username || 'No name available'}
					</p>
				</div>
			</div>

			<div class="flex items-center space-x-2 h-5">
				{#if profile?.isCurrentUser}<SleekButton onclick={toggleEdit}>
						{#if !canEdit}Edit{:else}Done{/if}
					</SleekButton>
				{/if}
			</div>
		</div>
	</PanelHeader>

	<PanelBody>
		<div class="py-4 flex flex-col text-black dark:text-white w-full">
			<ProfilePicture
				{isImmutable}
				currentProfilePicture={profile?.currentProfilePictureUrl}
				isVerified={profile?.hasStore}
			/>
			<div class="flex flex-col mt-4 space-y-4 px-4">
				<div>
					<!-- <p class="text-xs text-brand-gray-4">Name</p> -->
					{#if isImmutable}
						<ImmutableTitle title={profile?.currentName} class="pb-[6px]" />
					{:else}
						<TitleContainer
							title={profile?.currentName}
							onupdateTitle={updateName}
							errorOnNull={false}
							class="mt-2 lg:mt-2"
						/>
					{/if}
				</div>

				{#if profile?.hasStore}
					<SocialAccounts
						instagram={profile?.instagramUsername}
						rss={profile?.websiteUrl}
						tiktok={profile?.tiktokUsername}
						youtube={profile?.youtubeUsername}
						{isImmutable}
						onupdateSocialUsername={updateSocialUsername}
					/>
				{/if}
				<div>
					<p class="text-xs text-brand-gray-4">Profile Bio</p>

					{#if $profileStore?.fetching}
						<Spinner size="sm" />
					{:else if isImmutable}
						{#key profile?.id}
							<DescriptionDocument
								description={profile?.bio == '' || profile?.bio === null
									? 'no bio provided'
									: profile?.bio}
							/>
						{/key}
					{:else}
						<DescriptionForm
							initialContent={profile?.bio == '' || profile?.bio === null
								? 'no bio provided'
								: profile?.bio}
							onchange={handleProfileBioUpdate}
						/>
					{/if}
				</div>
			</div>

			{#if profile?.publishedListings.length > 0}
				<StickyHeader
					class="w-full mb-2 top-[64px] lg:top-16 px-4"
					buttonClass="dark:hover:text-brand-gray-2 cursor-auto"
					title="Published Guides"
				/>

				<div class="w-full mb-4 mt-2 flex flex-col">
					{#each profile?.publishedListings as listing, index}
						{#if index !== 0}
							<div class="h-[1px] rounded-full my-6 bg-brand-gray-4/25">
								<!-- space -->
							</div>
						{/if}
						<ExploreTripCard
							trip={listing?.currentTrip}
							basePrice={listing?.basePrice}
							currency={listing?.currency}
							fetching={nextIntentID === listing?.currentTrip?.id}
							onclick={handleClickTrip}
							onmouseenter={preloadListingPage}
							weekendMinCost={listing?.currentTrip?.weekendCostRangeLower}
							weekendMaxCost={listing?.currentTrip?.weekendCostRangeUpper}
							weekMinCost={listing?.currentTrip?.weekCostRangeLower}
							weekMaxCost={listing?.currentTrip?.weekCostRangeUpper}
							monthMinCost={listing?.currentTrip?.monthCostRangeLower}
							monthMaxCost={listing?.currentTrip?.monthCostRangeUpper}
							categories={listing?.currentTrip?.categories}
							avgReviewRating={listing?.avgRating}
							numReviews={listing?.numReviews}
						/>
					{/each}
				</div>
			{/if}

			<div class="mt-6">
				<StickyHeader
					class="w-full mb-2 px-4"
					buttonClass="dark:hover:text-brand-gray-2 cursor-auto"
					title="Reviews"
				/>
				{#if reviews?.length > 0}
					<div class="w-full px-4">
						<ReviewsList {reviews} />
					</div>
				{:else}
					<div class="mt-6 px-4">
						<p class="text-sm text-brand-gray-4">No reviews for this creator, yet.</p>
					</div>
				{/if}
			</div>
		</div>
	</PanelBody>
{/if}
